import React from 'react';
import { css } from '@emotion/core';
import { DropdownField, useControlledField } from '@weave/design-system';
import { useQuery } from 'react-query';
import { CustomAxios, getResponseData } from 'redux/axios';
import { UserModel } from 'models/user.model';
import { usersApi } from 'redux/actions/users/users.api';

export interface PocDropdownProps {
  locationId: string;
  value: string;
  onChange: (value: string) => void;
  showValidationErrors?: boolean;
  disabled?: boolean;
}

export const PocDropdown: React.FC<PocDropdownProps> = ({
  locationId,
  value,
  onChange,
  showValidationErrors = false,
  disabled = false,
}) => {
  const { data: users = [], isLoading } = useQuery<UserModel[]>({
    queryKey: [locationId, 'unifyLocationUsers'],
    queryFn: async () => {
      return usersApi.getUsers(100, locationId);
    },
    enabled: !!locationId && !disabled,
    retry: false,
    refetchOnMount: 'always',
  });

  const controlledField = useControlledField({
    type: 'dropdown',
    value: value || '',
    required: true,
    onChange: (val) => onChange(val ?? ''),
  });

  const hasError = showValidationErrors && !value && !disabled;

  return (
    <div css={styles.container}>
      <DropdownField
        {...controlledField}
        name={`poc-${locationId}`}
        label="Point of Contact"
        placeholder={isLoading ? 'Loading...' : 'Select one'}
        disabled={disabled || isLoading}
        touched={hasError ? true : controlledField.touched}
        aria-invalid={hasError ? true : controlledField['aria-invalid']}
        error={hasError ? 'Point of Contact is required' : ''}
      >
        {users.map((user) => {
          const fullName = `${user.FirstName || ''} ${user.LastName || ''}`.trim();
          const displayLabel = fullName
            ? user.Username
              ? `${fullName} (${user.Username})`
              : fullName
            : user.Username || user.UserID;
          const optionValue = user.Username || user.UserID;

          return (
            <DropdownField.Option key={user.UserID || user.Username} value={optionValue}>
              {displayLabel}
            </DropdownField.Option>
          );
        })}
      </DropdownField>
    </div>
  );
};

const styles = {
  container: css`
    width: 100%;
    min-width: 220px;
    max-width: 320px;
  `,
};
